import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  Alert,
  Rating,
  Avatar,
  Divider,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  LocationOn,
  Store,
  Star,
  ShoppingBag,
  Navigation,
  Phone,
  AccessTime,
} from '@mui/icons-material';
import axios from 'axios';

const StoreLocator = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchPincode, setSearchPincode] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);

  useEffect(() => {
    // Try to get user's location
    getUserLocation();

    // Load default stores based on customer's saved pincode or saved location
    const customerData = localStorage.getItem('customerData');
    const savedPincode = localStorage.getItem('preferredPincode');

    if (customerData) {
      try {
        const customer = JSON.parse(customerData);
        // Use customer's preferred pincode if available
        if (customer.preferred_pincode) {
          setSearchPincode(customer.preferred_pincode);
          fetchStores(customer.preferred_pincode);
          return;
        }
      } catch (e) {
        console.log('Error parsing customer data:', e);
      }
    }

    // Fallback to saved pincode or show all stores
    if (savedPincode) {
      setSearchPincode(savedPincode);
      fetchStores(savedPincode);
    } else {
      // Show all stores if no location preference
      fetchStores();
    }
  }, []);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  };

  const fetchStores = async (pincode = null, latitude = null, longitude = null) => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (pincode) params.pincode = pincode;
      if (latitude && longitude) {
        params.latitude = latitude;
        params.longitude = longitude;
      }

      console.log('🏪 Fetching stores with params:', params);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/marketplace/stores`, { params });

      console.log('🏪 Stores response:', response.data);
      if (response.data.success) {
        setStores(response.data.stores);
        console.log(`🏪 Loaded ${response.data.stores.length} stores`);
      }
    } catch (err) {
      setError('Failed to load stores. Please try again.');
      console.error('Error fetching stores:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePincodeSearch = () => {
    if (searchPincode.trim()) {
      fetchStores(searchPincode.trim());
      localStorage.setItem('preferredPincode', searchPincode.trim());
    }
  };

  const handleLocationSearch = () => {
    if (userLocation) {
      fetchStores(null, userLocation.latitude, userLocation.longitude);
    } else {
      setError('Location access required. Please enable location services.');
    }
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store);
    setStoreDialogOpen(true);
  };

  const StoreCard = ({ store }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
      onClick={() => handleStoreClick(store)}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={store.business_logo}
            sx={{
              width: 50,
              height: 50,
              mr: 2,
              bgcolor: 'primary.main'
            }}
          >
            <Store />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {store.business_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating value={store.averageRating} readOnly size="small" />
              <Typography variant="body2" color="text.secondary">
                ({store.totalRatings} reviews)
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          📍 {store.address}, {store.city}, {store.state} {store.pincode}
        </Typography>

        {store.distance_km && (
          <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
            📏 {store.distance_km.toFixed(1)} km away
          </Typography>
        )}

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            {store.delivery_available && (
              <Chip label="🚚 Delivery Available" size="small" color="success" variant="outlined" />
            )}
            {store.minimum_order_amount > 0 && (
              <Chip
                label={`₹${store.minimum_order_amount} min order`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Button
            variant="contained"
            fullWidth
            startIcon={<ShoppingBag />}
            onClick={(e) => {
              e.stopPropagation();
              // Use React Router navigation instead of window.location.href
              window.location.href = `/products?storeId=${store.business_id}`;
            }}
          >
            Shop Now
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const StoreDetailsDialog = () => (
    <Dialog
      open={storeDialogOpen}
      onClose={() => setStoreDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar src={selectedStore?.business_logo} sx={{ width: 40, height: 40 }}>
          <Store />
        </Avatar>
        {selectedStore?.business_name}
      </DialogTitle>
      <DialogContent>
        {selectedStore && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Rating value={selectedStore.averageRating} readOnly />
              <Typography variant="body2" color="text.secondary">
                ({selectedStore.totalRatings} reviews)
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {selectedStore.store_description || 'Welcome to our store!'}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>Store Details</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <LocationOn color="action" />
                <Typography variant="body2">
                  {selectedStore.address}, {selectedStore.city}, {selectedStore.state} {selectedStore.pincode}
                </Typography>
              </Box>

              {selectedStore.distance_km && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Navigation color="action" />
                  <Typography variant="body2">
                    {selectedStore.distance_km.toFixed(1)} km away
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTime color="action" />
                <Typography variant="body2">
                  Delivery Radius: {selectedStore.delivery_radius_km} km
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedStore.delivery_available && (
                <Chip label="🚚 Home Delivery" color="success" size="small" />
              )}
              {selectedStore.minimum_order_amount > 0 && (
                <Chip
                  label={`₹${selectedStore.minimum_order_amount} Min Order`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setStoreDialogOpen(false)}>Close</Button>
        <Button
          variant="contained"
          onClick={() => {
            setStoreDialogOpen(false);
            window.location.href = `/products?storeId=${selectedStore.business_id}`;
          }}
          startIcon={<ShoppingBag />}
        >
          Shop Now
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Find Stores Near You</Typography>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={50} height={50} sx={{ mb: 2 }} />
                  <Skeleton variant="text" height={30} />
                  <Skeleton variant="text" height={20} width="60%" />
                  <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
          Find Stores Near You
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Discover local stores and shop from your favorite vendors
        </Typography>

        {/* Search Controls */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
          <TextField
            placeholder="Enter pincode (e.g., 110001)"
            value={searchPincode}
            onChange={(e) => setSearchPincode(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePincodeSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <Button
            variant="contained"
            onClick={handlePincodeSearch}
            disabled={!searchPincode.trim()}
          >
            Search by Pincode
          </Button>

          <Button
            variant="outlined"
            onClick={handleLocationSearch}
            startIcon={<LocationOn />}
            disabled={!userLocation}
          >
            Use My Location
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Results Summary */}
      {stores.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Found {stores.length} store{stores.length !== 1 ? 's' : ''} in your area
        </Typography>
      )}

      {/* Stores Grid */}
      <Grid container spacing={3}>
        {stores.map((store) => (
          <Grid item xs={12} sm={6} md={4} key={store.business_id}>
            <StoreCard store={store} />
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {stores.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Store sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No stores found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Try searching with a different pincode or enable location access
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setSearchPincode('')}>
              Clear Search
            </Button>
            <Button variant="outlined" onClick={handleLocationSearch}>
              Use My Location
            </Button>
          </Box>
        </Box>
      )}

      {/* Store Details Dialog */}
      <StoreDetailsDialog />
    </Box>
  );
};

export default StoreLocator;